import React from 'react'

export type PageHeaderConfig = {
  title: string
  description?: string
  actions?: React.ReactNode
  titleClassName?: string | null
  hideBackButton?: boolean
  backTo?: string
}

type PageHeaderContextValue = {
  config: PageHeaderConfig | null
  setPageHeader: (patch: Partial<PageHeaderConfig>) => void
  clearPageHeader: () => void
}

const PageHeaderContext = React.createContext<PageHeaderContextValue | null>(null)

export const PageHeaderProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [config, setConfig] = React.useState<PageHeaderConfig | null>(null)

  const setPageHeader = React.useCallback((patch: Partial<PageHeaderConfig>) => {
    setConfig((prev) => {
      const next: PageHeaderConfig = {
        title: patch.title ?? prev?.title ?? '',
        description: patch.description !== undefined ? patch.description : prev?.description,
        actions: patch.actions !== undefined ? patch.actions : prev?.actions,
        titleClassName: patch.titleClassName !== undefined ? patch.titleClassName : prev?.titleClassName,
        hideBackButton: patch.hideBackButton !== undefined ? patch.hideBackButton : prev?.hideBackButton,
        backTo: patch.backTo !== undefined ? patch.backTo : prev?.backTo,
      }
      if (
        prev
        && prev.title === next.title
        && prev.description === next.description
        && prev.actions === next.actions
        && prev.titleClassName === next.titleClassName
        && prev.hideBackButton === next.hideBackButton
        && prev.backTo === next.backTo
      ) {
        return prev
      }
      return next
    })
  }, [])

  const clearPageHeader = React.useCallback(() => {
    setConfig(null)
  }, [])

  const value = React.useMemo(
    () => ({ config, setPageHeader, clearPageHeader }),
    [config, setPageHeader, clearPageHeader]
  )

  return (
    <PageHeaderContext.Provider value={value}>
      {children}
    </PageHeaderContext.Provider>
  )
}

export function usePageHeader(): PageHeaderContextValue {
  const ctx = React.useContext(PageHeaderContext)
  if (!ctx) {
    throw new Error('usePageHeader must be used within PageHeaderProvider')
  }
  return ctx
}

/** Returns null when used outside PageHeaderProvider (e.g. tests). */
export function usePageHeaderOptional(): PageHeaderContextValue | null {
  return React.useContext(PageHeaderContext)
}
